import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import { useAuth } from '../auth/useAuth'
import EmptyState from '../components/EmptyState'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'

interface User {
  username: string
  email: string
  status: string
  enabled: boolean
  created: string
  groups: string[]
}

interface UsersResponse {
  users: User[]
  count: number
}

// Los 3 roles del rediseño de set-2026. Reemplazan a Admin/Operator/Reader:
// los grupos viejos ya no existen en el User Pool (ver
// scripts/migrar_roles_portal.py). Acá el nombre del grupo y la etiqueta que ve
// el usuario son EL MISMO: el modelo anterior mostraba "Analista" para el grupo
// "Operator" y nadie sabía cuál pedirle al admin.
const ALL_GROUPS = ['Admin', 'Supervisor', 'Asesor'] as const
type Group = typeof ALL_GROUPS[number]

const GROUP_LABEL: Record<Group, string> = {
  Admin: 'Admin',
  Supervisor: 'Supervisor',
  Asesor: 'Asesor',
}

const GROUP_HINT: Record<Group, string> = {
  Admin: 'Todo: configuración, usuarios, auditoría e integraciones',
  Supervisor: 'Cola global del equipo, asesores, documentos y métricas',
  Asesor: 'Solo sus conversaciones asignadas por round-robin',
}

const GROUP_COLORS: Record<Group, { bg: string; color: string }> = {
  Admin:      { bg: 'var(--c-danger-light)',   color: 'var(--c-danger-ink)' },
  Supervisor: { bg: 'var(--c-info-light)',  color: 'var(--c-info-ink)' },
  Asesor:     { bg: 'var(--c-brand-light)', color: 'var(--c-brand-hover)' },
}

const STATUS_LABELS: Record<string, string> = {
  CONFIRMED:             'Confirmado',
  UNCONFIRMED:           'Sin confirmar',
  FORCE_CHANGE_PASSWORD: 'Cambio de clave',
  RESET_REQUIRED:        'Reset requerido',
  COMPROMISED:           'Comprometido',
  EXTERNAL_PROVIDER:     'Proveedor ext.',
}

function fmtDate(ts?: string) {
  if (!ts) return '—'
  try { return new Date(ts).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }) }
  catch { return ts }
}

function initials(email: string) {
  const parts = email.split('@')[0].split(/[._-]/)
  return parts.map(p => p[0] ?? '').join('').slice(0, 2).toUpperCase() || '??'
}

interface InviteForm { email: string; group: Group }

/**
 * Rol único del usuario a partir de sus grupos de Cognito.
 *
 * Los 3 roles son EXCLUYENTES, pero un usuario puede tener grupos de más si
 * alguien los agregó desde la consola de AWS o con la API vieja. Se muestra el
 * de mayor alcance, igual que hace el portal para armar la navegación.
 */
function rolDe(groups: string[]): Group | null {
  for (const g of ALL_GROUPS) {
    if (groups.includes(g)) return g
  }
  return null
}

/**
 * Selector de rol: tres opciones, una activa. Elegir otra la reemplaza.
 *
 * Antes eran botones "+ rol" que ACUMULABAN, y se podía terminar con Admin y
 * Asesor a la vez. Eso confundía de verdad: el round-robin mira el grupo
 * Asesor de Cognito y le asignaba conversaciones, pero la navegación se arma
 * con el rol de mayor alcance y le mostraba el portal de admin.
 */
function SelectorRol({ actual, saving, usernameKey, onElegir, onQuitar }: {
  actual: Group | null
  saving: string | null
  usernameKey: string
  onElegir: (g: Group) => void
  onQuitar: (g: Group) => void
}) {
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
      {ALL_GROUPS.map(g => {
        const activo = actual === g
        const cargando = saving === `${usernameKey}:${g}:add`
        return (
          <button
            key={g}
            onClick={() => !activo && onElegir(g)}
            disabled={!!saving || activo}
            title={activo ? `Rol actual: ${GROUP_LABEL[g]}` : `Cambiar a ${GROUP_LABEL[g]}`}
            style={{
              all: 'unset',
              cursor: saving ? 'not-allowed' : activo ? 'default' : 'pointer',
              fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
              border: `1px ${activo ? 'solid' : 'dashed'} ${GROUP_COLORS[g].color}`,
              background: activo ? GROUP_COLORS[g].bg : 'transparent',
              color: GROUP_COLORS[g].color,
              opacity: saving && !cargando ? 0.5 : 1,
            }}
          >
            {cargando ? '…' : GROUP_LABEL[g]}
          </button>
        )
      })}
      {actual ? (
        <button
          onClick={() => onQuitar(actual)}
          disabled={!!saving}
          title="Quitar el rol: el usuario entra y ve 'Acceso pendiente'"
          style={{
            all: 'unset', cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: 11, color: 'var(--c-ink-muted)', marginLeft: 2,
          }}
        >
          quitar
        </button>
      ) : (
        <span style={{ fontSize: 11.5, color: 'var(--c-ink-faint)', marginLeft: 2 }}>
          sin acceso
        </span>
      )}
    </div>
  )
}

export default function Users() {
  const { isAdmin, user: currentUser } = useAuth()
  const [users, setUsers]       = useState<User[]>([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState<string | null>(null)
  const [search, setSearch]     = useState('')
  const [saving, setSaving]     = useState<string | null>(null)
  const [showInvite, setShowInvite] = useState(false)
  const [inviteForm, setInviteForm] = useState<InviteForm>({ email: '', group: 'Asesor' })
  const [inviting, setInviting] = useState(false)
  const [inviteOk, setInviteOk] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [syncOk, setSyncOk] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await apiClient.get<UsersResponse>('/users')
      setUsers(res.data.users)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar usuarios')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    if (isAdmin) void fetchUsers()
    else setLoading(false)
  }, [isAdmin, fetchUsers])

  const inviteUser = async () => {
    const { email, group } = inviteForm
    if (!email.trim()) return
    setInviting(true); setError(null); setInviteOk(null)
    try {
      await apiClient.post('/users/invite', { email: email.trim(), group })
      setInviteOk(`Invitación enviada a ${email.trim()} con rol ${group}`)
      setInviteForm({ email: '', group: 'Asesor' })
      await fetchUsers()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al invitar usuario')
    } finally { setInviting(false) }
  }

  const syncFederatedRoles = async () => {
    if (!window.confirm('Se consolidarán las cuentas Google duplicadas con su cuenta de acceso.')) return
    setSyncing(true); setError(null); setSyncOk(null)
    try {
      const res = await apiClient.post<{ consolidated: Array<{ email: string; groups_added: string[] }>, synced: Array<{ email: string; groups_added: string[] }>, skipped: unknown[] }>(
        '/users/sync-federated-roles', {}
      )
      const n = res.data.consolidated.length
      if (n === 0) {
        setSyncOk('Todo en orden: no había usuarios federated pendientes de sincronizar.')
      } else {
        const detalles = res.data.synced.map(s => `${s.email} → ${s.groups_added.join(', ')}`).join('; ')
        setSyncOk(`Sincronizados ${n}: ${detalles}`)
      }
      await fetchUsers()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al sincronizar roles')
    } finally { setSyncing(false) }
  }

  const deleteUser = async (u: User) => {
    if (currentUser?.email && u.email && u.email.toLowerCase() === currentUser.email.toLowerCase()) {
      setError('No puedes eliminar tu propia cuenta.')
      return
    }
    const label = u.email || u.username
    if (!window.confirm(`¿Eliminar al usuario ${label}? Esta acción es irreversible.`)) return
    setDeleting(u.username); setError(null); setSyncOk(null)
    try {
      await apiClient.delete(`/users/${encodeURIComponent(u.username)}`)
      setUsers(prev => prev.filter(x => x.username !== u.username))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al eliminar usuario')
    } finally { setDeleting(null) }
  }

  const updateRole = async (username: string, group: Group, action: 'add' | 'remove') => {
    const key = `${username}:${group}:${action}`
    setSaving(key)
    try {
      await apiClient.put(`/users/${encodeURIComponent(username)}/role`, { group, action })
      setUsers(prev => prev.map(u => {
        if (u.username !== username) return u
        // `add` REEMPLAZA, no acumula: es exactamente lo que hace el backend
        // (`_update_user_role` agrega el pedido y quita los otros dos). Cuando
        // acá se acumulaba, cambiar Supervisor -> Asesor dejaba el estado local
        // en ['Asesor','Supervisor'] y `rolDe` -que devuelve el de mayor
        // alcance- seguia pintando Supervisor: en Cognito el rol ya habia
        // cambiado, pero la pantalla mostraba que no, y se leia como "no me
        // deja cambiarlo". Solo se recargando aparecia el rol real.
        // Los grupos ajenos a los 3 roles se conservan, porque el backend
        // tampoco los toca.
        const groups = action === 'add'
          ? [...u.groups.filter(g => !ALL_GROUPS.includes(g as Group)), group]
          : u.groups.filter(g => g !== group)
        return { ...u, groups }
      }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al actualizar rol')
    } finally { setSaving(null) }
  }

  if (!isAdmin) {
    return (
      <div>
          <EmptyState icon="🔒" title="Acceso denegado"
          description="La gestión de usuarios está reservada para Administradores." />
      </div>
    )
  }

  const filtered = search.trim()
    ? users.filter(u =>
        u.email.toLowerCase().includes(search.toLowerCase()) ||
        u.username.toLowerCase().includes(search.toLowerCase())
      )
    : users

  const enabledCount = users.filter(u => u.enabled).length

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginTop: 4 }}>
          <button
            onClick={() => void syncFederatedRoles()}
            disabled={syncing}
            title="Copia roles a usuarios que entraron por Google y no tenían acceso"
            style={{
              all: 'unset', cursor: syncing ? 'default' : 'pointer',
              background: 'var(--c-info-light)', color: 'var(--c-info-ink)',
              fontWeight: 700, fontSize: 12.5,
              padding: '9px 14px', borderRadius: 9, opacity: syncing ? 0.5 : 1,
            }}
          >
            {syncing ? 'Sincronizando…' : '🔄 Sincronizar Google'}
          </button>
          <button
            onClick={() => { setShowInvite(true); setInviteOk(null); setError(null) }}
            style={{
              all: 'unset', cursor: 'pointer', background: 'var(--c-brand)',
              color: 'white', fontWeight: 700, fontSize: 13,
              padding: '9px 18px', borderRadius: 9,
            }}
          >
            + Invitar usuario
          </button>
        </div>
      </div>

      {syncOk && (
        <div style={{
          background: 'var(--c-brand-light)', border: '1px solid var(--c-brand-border)',
          borderRadius: 12, padding: '10px 14px', fontSize: 12.5,
          color: 'var(--c-brand-ink)', marginBottom: 10,
        }}>
          {syncOk}
        </div>
      )}

      {/* Modal invitación */}
      {showInvite && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999,
        }} onClick={() => setShowInvite(false)}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '28px 32px', width: 380,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--c-ink)', marginBottom: 20 }}>
              Invitar nuevo usuario
            </div>

            {inviteOk && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 14,
                background: 'var(--c-brand-light)', color: 'var(--c-brand-ink)', fontSize: 13,
              }}>{inviteOk}</div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-ink-muted)', display: 'block', marginBottom: 5 }}>
                  Email corporativo
                </label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') void inviteUser() }}
                  placeholder="nombre@prestamype.com"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    border: '1px solid var(--c-surface-strong)', borderRadius: 8,
                    padding: '9px 12px', fontSize: 14, fontFamily: 'inherit',
                  }}
                  autoFocus
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-ink-muted)', display: 'block', marginBottom: 5 }}>
                  Rol inicial
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {ALL_GROUPS.map(g => (
                    <button key={g} onClick={() => setInviteForm(f => ({ ...f, group: g }))}
                      style={{
                        all: 'unset', cursor: 'pointer', padding: '7px 14px', borderRadius: 8,
                        fontSize: 13, fontWeight: 600,
                        border: `2px solid ${inviteForm.group === g ? GROUP_COLORS[g].color : 'var(--c-surface-strong)'}`,
                        background: inviteForm.group === g ? GROUP_COLORS[g].bg : 'white',
                        color: inviteForm.group === g ? GROUP_COLORS[g].color : 'var(--c-ink-muted)',
                      }}>{GROUP_LABEL[g]}</button>
                  ))}
                </div>
                {/* Qué puede hacer cada rol. Sin esto el admin elige entre tres
                    palabras sin saber qué habilita cada una. */}
                <p style={{ marginTop: 8, fontSize: 11.5, color: 'var(--c-ink-muted)' }}>
                  {GROUP_HINT[inviteForm.group]}
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowInvite(false)}
                style={{
                  all: 'unset', cursor: 'pointer', padding: '9px 18px', borderRadius: 8,
                  border: '1px solid var(--c-surface-strong)', fontSize: 13, color: 'var(--c-ink-soft)',
                }}>Cancelar</button>
              <button
                onClick={() => void inviteUser()}
                disabled={inviting || !inviteForm.email.trim()}
                style={{
                  all: 'unset', cursor: inviting || !inviteForm.email.trim() ? 'not-allowed' : 'pointer',
                  padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  background: 'var(--c-brand)', color: 'white',
                  opacity: inviting || !inviteForm.email.trim() ? 0.5 : 1,
                }}>{inviting ? 'Enviando…' : 'Enviar invitación'}</button>
            </div>
          </div>
        </div>
      )}

      {error && <ErrorAlert message={error} />}

      {!loading && !error && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total usuarios', value: users.length },
            { label: 'Activos', value: enabledCount },
            { label: 'Inactivos', value: users.length - enabledCount },
          ].map(s => (
            <div key={s.label} style={{
              background: '#fff', border: '1px solid var(--c-surface-border)',
              borderRadius: 10, padding: '12px 20px', minWidth: 110,
            }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--c-ink)' }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--c-ink-muted)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Buscar por email o usuario…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            padding: '8px 14px', borderRadius: 8,
            border: '1px solid var(--c-surface-strong)',
            fontSize: 14, width: 320, outline: 'none',
          }}
        />
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filtered.length === 0 ? (
        <EmptyState icon="👥" title="Sin usuarios"
          description={search ? 'Ningún usuario coincide con la búsqueda.' : 'No hay usuarios registrados en el pool.'} />
      ) : (
        <>
          <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid var(--c-surface-border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'var(--c-surface-subtle)', borderBottom: '1px solid var(--c-surface-border)' }}>
                  {['Usuario', 'Email', 'Estado', 'Rol', 'Creado', ''].map(h => (
                    <th key={h} style={{
                      padding: '10px 16px', textAlign: 'left',
                      fontWeight: 600, color: 'var(--c-ink-muted)', fontSize: 13, whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u, i) => (
                  <tr key={u.username} style={{
                    borderBottom: '1px solid var(--c-surface-faint)',
                    background: i % 2 === 0 ? '#fff' : 'var(--c-surface-alt)',
                  }}>
                    {/* Usuario */}
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: 'var(--c-info-light)', color: 'var(--c-info-ink)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 700, fontSize: 13, flexShrink: 0,
                        }}>
                          {initials(u.email || u.username)}
                        </div>
                        <span style={{ fontWeight: 500, color: 'var(--c-ink)', fontSize: 13 }}>
                          {u.email || u.username}
                        </span>
                      </div>
                    </td>

                    {/* Email */}
                    <td style={{ padding: '10px 16px', color: 'var(--c-ink-soft)', fontSize: 13 }}>
                      {u.email || '—'}
                    </td>

                    {/* Estado */}
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: u.enabled ? 'var(--c-brand-hover)' : 'var(--c-danger)' }}>
                        {STATUS_LABELS[u.status] ?? u.status}
                      </span>
                    </td>

                    {/* Rol — uno solo, excluyente. Clic en otro lo reemplaza. */}
                    <td style={{ padding: '10px 16px' }}>
                      <SelectorRol
                        actual={rolDe(u.groups)}
                        saving={saving}
                        usernameKey={u.username}
                        onElegir={(g) => void updateRole(u.username, g, 'add')}
                        onQuitar={(g) => void updateRole(u.username, g, 'remove')}
                      />
                    </td>

                    {/* Creado */}
                    <td style={{ padding: '10px 16px', color: 'var(--c-ink-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
                      {fmtDate(u.created)}
                    </td>
                    {/* Acciones — eliminar */}
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      {(() => {
                        const isSelf = currentUser?.email && u.email &&
                          currentUser.email.toLowerCase() === u.email.toLowerCase()
                        return (
                          <button
                            onClick={() => void deleteUser(u)}
                            disabled={deleting === u.username || Boolean(isSelf)}
                            title={isSelf ? 'No puedes eliminarte a ti mismo' : 'Eliminar usuario'}
                            style={{
                              all: 'unset',
                              cursor: (deleting === u.username || isSelf) ? 'default' : 'pointer',
                              fontSize: 11.5, fontWeight: 700,
                              color: 'var(--c-danger-ink)',
                              background: 'var(--c-danger-light)',
                              padding: '4px 10px', borderRadius: 8,
                              opacity: (deleting === u.username || isSelf) ? 0.4 : 1,
                              whiteSpace: 'nowrap',
                            }}>
                            {deleting === u.username ? '…' : 'Eliminar'}
                          </button>
                        )
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--c-ink-muted)' }}>
            {filtered.length} usuario{filtered.length !== 1 ? 's' : ''}{search ? ' (filtrados)' : ''}
          </div>
        </>
      )}

      <div style={{
        marginTop: 24, padding: '12px 16px',
        background: 'var(--c-info-tint)', borderRadius: 10,
        border: '1px solid var(--c-info-border)',
        fontSize: 13, color: 'var(--c-info-ink)',
      }}>
        <strong>Un rol por usuario.</strong> Elegir uno reemplaza el anterior: el portal arma la
        navegación con un solo rol, y tener dos hacía que el round-robin y el sidebar no
        coincidieran. Se aplica en Cognito al instante, pero el usuario lo ve en su próximo
        login: el rol viaja dentro del token.
        <br />
        <strong>Admin</strong> — todo: configuración, usuarios, auditoría e integraciones ·{' '}
        <strong>Supervisor</strong> — cola global del equipo, asesores, documentos y métricas ·{' '}
        <strong>Asesor</strong> — solo sus conversaciones asignadas por round-robin
      </div>
    </div>
  )
}
